import errorIcon from "assets/images/error-icon.svg";
import classNames from "classnames";
import React, {useState} from "react";
import {dateRangeSelect as strings} from "strings";

import DateInput, {DateString} from "components/DateInput";
import Select from "components/Select";

import styles from "./DateRangeSelect.module.css";

export type DateRangeOption =
  | "last-7-days"
  | "last-30-days"
  | "last-90-days"
  | "last-6-months"
  | "last-12-months"
  | "last-24-months"
  | "custom";

export const DEFAULT_DATE_FILTER = "last-90-days";
export const DATE_RANGE_OPTIONS: {label: string; value: DateRangeOption}[] = [
  {label: strings.lastDays(7), value: "last-7-days"},
  {label: strings.lastDays(30), value: "last-30-days"},
  {label: strings.lastDays(90), value: "last-90-days"},
  {label: strings.lastMonths(6), value: "last-6-months"},
  {label: strings.lastMonths(12), value: "last-12-months"},
  {label: strings.lastMonths(24), value: "last-24-months"},
  {label: strings.custom, value: "custom"},
];

export type DateRange = `${DateString}to${DateString}`;

const getCustomStartDate = (
  dateRange: DateRangeOption | DateRange,
): DateString => {
  const splitDate = dateRange.split("to");
  if (splitDate.length < 2) return "";

  return splitDate[0] as DateString;
};

const getCustomEndDate = (
  dateRange: DateRangeOption | DateRange,
): DateString => {
  const splitDate = dateRange.split("to");
  if (splitDate.length < 2) return "";

  return splitDate[1] as DateString;
};

type Props = {
  className?: string;
  setDateFilter: (value: DateRangeOption | DateRange) => void;
  dateRangeSelect: DateRangeOption;
  setDateRangeSelect: (value: DateRangeOption) => void;
};

export default function DateRangeSelect({
  className,
  setDateFilter,
  dateRangeSelect,
  setDateRangeSelect,
}: Props) {
  const [customStartDate, setCustomStartDate] = useState(
    getCustomStartDate(DEFAULT_DATE_FILTER),
  );
  const [customEndDate, setCustomEndDate] = useState(
    getCustomEndDate(DEFAULT_DATE_FILTER),
  );

  return (
    <div className={classNames(styles.container, className)}>
      <Select
        options={DATE_RANGE_OPTIONS}
        value={dateRangeSelect}
        placeholder={strings.selectRange}
        onChange={(value) => {
          setDateRangeSelect(value);
          if (value === "custom")
            setDateFilter(`${customStartDate}to${customEndDate}`);
          else setDateFilter(value);
        }}
      />
      {dateRangeSelect === "custom" ? (
        <div className={styles.customRangeControls}>
          <span className={styles.customRangePadding}>{strings.from}</span>
          <DateInput
            className={styles.dateInput}
            value={customStartDate}
            onChangeDate={(value: DateString) => {
              setCustomStartDate(value);
              setDateFilter(`${value}to${customEndDate}`);
            }}
          />
          <span className={styles.customRangePadding}>{strings.to}</span>
          <DateInput
            className={styles.dateInput}
            value={customEndDate}
            onChangeDate={(value: DateString) => {
              setCustomEndDate(value);
              setDateFilter(`${customStartDate}to${value}`);
            }}
          />
        </div>
      ) : null}
      {dateRangeSelect === "custom" &&
      customStartDate !== "" &&
      customEndDate !== "" &&
      customStartDate.localeCompare(customEndDate) > 0 ? (
        <div className={styles.errorContainer}>
          <img src={errorIcon} alt="" />
          <div className={styles.errorMessage}>{strings.errorMessage}</div>
        </div>
      ) : null}
    </div>
  );
}
